import { any, filter, identity, propEq, propOr } from 'ramda';
import { isPTIARequired } from '@@viewHelpers/validations/guaranteesPrevTns.js';
import { isNotNilOrEmpty } from '@@serverHelpers';
import { useMemo } from 'react';
import { getReimbursementGraphContent } from './helpers/getReimbursementGraphContent.js';

const useNiveauxGarantiesTarifsPrevTnsGarantiesDecesForm = ({
  form,
  guarantees,
  getMiddleText,
  getMiddleBoolean,
  annualIncome
}) => {
  const {
    OPTION_TT_CAUSE,
    OPTION_ACC,
    TT_CAUSE_CAP,
    ACC_CAP,
    TT_CAUSE_DEP,
    ACC_DEP,
    TT_CAUSE_MAD,
    ACC_MAD,
    OPTION_TT_CAUSE_DOUBL_ACC,
    OPTION_TT_CAUSE_DOUBL_EFFET,
    OPTION_ACC_DOUBL_ACC,
    OPTION_ACC_DOUBL_EFFET
  } = guarantees;

  const ptiaTTCause = form.watch('guarantees.OPTION_TT_CAUSE.selected');
  const ptiaAcc = form.watch('guarantees.OPTION_ACC.selected');
  const accDoubleEffet = form.watch('guarantees.OPTION_ACC_DOUBL_EFFET.selected');
  const accDoubleAcc = form.watch('guarantees.OPTION_ACC_DOUBL_ACC.selected');
  const ttCauseDoubleEffet = form.watch('guarantees.OPTION_TT_CAUSE_DOUBL_EFFET.selected');
  const ttCauseDoubleAcc = form.watch('guarantees.OPTION_TT_CAUSE_DOUBL_ACC.selected');
  const ttCauseCap = form.watch('guarantees.TT_CAUSE_CAP.value');
  const accCap = form.watch('guarantees.ACC_CAP.value');

  // Calcul dynamique de la part assureur pour TT_CAUSE_CAP (pour affichage uniquement)
  const ttCauseHealthInsurances = useMemo(() => {
    if (!ptiaTTCause || !ttCauseCap) return [];

    const percentage = parseFloat(ttCauseCap) || 0;
    const insurerAmount = annualIncome * (percentage / 100);

    return [insurerAmount.toString()];
  }, [ptiaTTCause, ttCauseCap, annualIncome]);

  // Calcul dynamique de la part assureur pour OPTION_ACC (pour affichage uniquement)
  const accHealthInsurances = useMemo(() => {
    if (!ptiaAcc || !accCap) return [];

    const percentage = parseFloat(accCap) || 0;
    const insurerAmount = annualIncome * (percentage / 100);

    return [insurerAmount.toString()];
  }, [ptiaAcc, accCap, annualIncome]);

  const anyPTIATrue = any(identity)([ptiaTTCause, ptiaAcc]);

  const deceasedAndPTIACotisation = [
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_EST_NECESSAIRE') &&
        ptiaTTCause &&
        isNotNilOrEmpty(TT_CAUSE_CAP) &&
        isNotNilOrEmpty(ttCauseCap),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_TTCAUSE_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', TT_CAUSE_CAP),
      yearlyAmount: propOr(0.0, 'annualPrice', TT_CAUSE_CAP)
    },
    {
      displayed:
        getMiddleBoolean('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_EST_NECESSAIRE') &&
        ptiaAcc &&
        isNotNilOrEmpty(ACC_CAP) &&
        isNotNilOrEmpty(accCap),
      label: getMiddleText('NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_ACC_LABEL'),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_ACC),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_ACC)
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_EST_NECESSAIRE'
        ) &&
        ptiaAcc &&
        isNotNilOrEmpty(OPTION_ACC_DOUBL_ACC) &&
        accDoubleAcc,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_LABEL'
      ),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_ACC_DOUBL_ACC),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_ACC_DOUBL_ACC)
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_EST_NECESSAIRE'
        ) &&
        ptiaTTCause &&
        isNotNilOrEmpty(OPTION_TT_CAUSE_DOUBL_ACC) &&
        ttCauseDoubleAcc,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_ACC_LABEL'
      ),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_TT_CAUSE_DOUBL_ACC),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_TT_CAUSE_DOUBL_ACC)
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_EST_NECESSAIRE'
        ) &&
        ptiaAcc &&
        isNotNilOrEmpty(OPTION_ACC_DOUBL_EFFET) &&
        accDoubleEffet,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_LABEL'
      ),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_ACC_DOUBL_EFFET),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_ACC_DOUBL_EFFET)
    },
    {
      displayed:
        getMiddleBoolean(
          'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_EST_NECESSAIRE'
        ) &&
        ptiaTTCause &&
        isNotNilOrEmpty(OPTION_TT_CAUSE_DOUBL_EFFET) &&
        ttCauseDoubleEffet,
      label: getMiddleText(
        'NIVEAUXGARANTIESETTARIFS_GAR_DECES_DECES_PTIA_OPTION_DECES_DOUBL_EFFET_LABEL'
      ),
      monthlyAmount: propOr(0.0, 'monthlyPrice', OPTION_TT_CAUSE_DOUBL_EFFET),
      yearlyAmount: propOr(0.0, 'annualPrice', OPTION_TT_CAUSE_DOUBL_EFFET)
    }
  ];

  const ptiaTTCauseReimbursementGraphContent = getReimbursementGraphContent(
    ptiaTTCause ? ttCauseHealthInsurances : accHealthInsurances,
    ptiaTTCause ? propOr([], 'labelsRO', TT_CAUSE_CAP) : propOr([], 'labelsRO', OPTION_ACC),
    ptiaTTCause ? propOr([], 'RO', TT_CAUSE_CAP) : propOr([], 'RO', OPTION_ACC)
  );

  return {
    ptiaTTCause,
    ptiaAcc,
    anyPTIATrue,
    isPTIARequired,
    deceasedAndPTIACotisation: filter(propEq('displayed', true), deceasedAndPTIACotisation),
    OPTION_TT_CAUSE,
    OPTION_ACC,
    TT_CAUSE_CAP,
    ACC_CAP,
    TT_CAUSE_DEP,
    ACC_DEP,
    TT_CAUSE_MAD,
    ACC_MAD,
    OPTION_ACC_DOUBL_ACC,
    OPTION_ACC_DOUBL_EFFET,
    OPTION_TT_CAUSE_DOUBL_ACC,
    OPTION_TT_CAUSE_DOUBL_EFFET,
    ttCauseHealthInsurances,
    accHealthInsurances,
    ptiaTTCauseReimbursementGraphContent
  };
};

export default useNiveauxGarantiesTarifsPrevTnsGarantiesDecesForm;
